import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

// Right sidebar width + 8px gap — keeps default position in the canvas area
const RIGHT_SIDEBAR_OFFSET = 348;

interface FloatingEditorPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  width?: number;
  height?: number;
  defaultPosition?: { x: number; y: number };
  children: React.ReactNode;
}

export const FloatingEditorPanel: React.FC<FloatingEditorPanelProps> = ({
  open,
  onClose,
  title,
  width = 304,
  height = 500,
  defaultPosition,
  children,
}) => {
  const [pos, setPos] = useState<{ x: number; y: number }>(
    () => defaultPosition ?? { x: Math.max(8, window.innerWidth - width - RIGHT_SIDEBAR_OFFSET), y: 60 },
  );
  const [isDragging, setIsDragging] = useState(false);
  const dragging = useRef(false);
  const origin = useRef({ mouseX: 0, mouseY: 0, panelX: 0, panelY: 0 });

  useEffect(() => {
    document.body.style.cursor = isDragging ? "grabbing" : "";
    return () => {
      document.body.style.cursor = "";
    };
  }, [isDragging]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      setPos({
        x: origin.current.panelX + e.clientX - origin.current.mouseX,
        y: origin.current.panelY + e.clientY - origin.current.mouseY,
      });
    };
    const onUp = () => {
      dragging.current = false;
      setIsDragging(false);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    window.addEventListener("blur", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      window.removeEventListener("blur", onUp);
    };
  }, []);

  const handleHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement && e.target.closest("button")) return;
    dragging.current = true;
    origin.current = { mouseX: e.clientX, mouseY: e.clientY, panelX: pos.x, panelY: pos.y };
    setIsDragging(true);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed z-50 rounded-lg border border-border bg-card shadow-2xl overflow-hidden flex flex-col"
      style={{ left: pos.x, top: pos.y, width, height }}
    >
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-border bg-card shrink-0 select-none cursor-grab"
        onMouseDown={handleHeaderMouseDown}
      >
        <span className="text-sm font-medium text-foreground">{title}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close ${title} panel`}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
    </div>,
    document.body,
  );
};
